"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Button,
  Input,
  PasswordInput,
  Label,
  FieldHint,
  Checkbox,
  Alert,
} from "@/components/ui";
import { registerSchema } from "@/schemas/auth";
import { apiPost, ApiClientError } from "@/lib/api-client";
import type { SafeUser } from "@/types/auth";

export function RegisterForm({ next, refCode }: { next?: string; refCode?: string }) {
  const router = useRouter();
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setErrors({});

    const form = new FormData(e.currentTarget);
    const raw = {
      fullName: String(form.get("fullName") ?? ""),
      email: String(form.get("email") ?? ""),
      password: String(form.get("password") ?? ""),
      teachingGrade: form.get("teachingGrade") ? Number(form.get("teachingGrade")) : undefined,
      acceptTerms: form.get("acceptTerms") === "on",
      ...(refCode ? { ref: refCode } : {}),
    };

    const parsed = registerSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0]);
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }

    setSubmitting(true);
    try {
      await apiPost<{ user: SafeUser }>("/api/auth/register", parsed.data);
      router.push(next || "/bang-dieu-khien");
      router.refresh();
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.fieldErrors) setErrors(err.fieldErrors);
        else setFormError(err.message);
      } else {
        setFormError("Không thể kết nối máy chủ. Vui lòng thử lại.");
      }
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {formError && <Alert variant="danger">{formError}</Alert>}

      <div>
        <Label htmlFor="fullName" required>
          Họ và tên
        </Label>
        <Input
          id="fullName"
          name="fullName"
          autoComplete="name"
          placeholder="Ví dụ: Nguyễn Thị Lan"
          invalid={Boolean(errors.fullName)}
          aria-describedby={errors.fullName ? "fullName-error" : undefined}
          className="mt-2"
        />
        {errors.fullName && (
          <FieldHint id="fullName-error" error>
            {errors.fullName}
          </FieldHint>
        )}
      </div>

      <div>
        <Label htmlFor="email" required>
          Email
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="ban@example.com"
          invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? "email-error" : undefined}
          className="mt-2"
        />
        {errors.email && (
          <FieldHint id="email-error" error>
            {errors.email}
          </FieldHint>
        )}
      </div>

      <div>
        <Label htmlFor="password" required>
          Mật khẩu
        </Label>
        <PasswordInput
          id="password"
          name="password"
          autoComplete="new-password"
          placeholder="Ít nhất 8 ký tự"
          invalid={Boolean(errors.password)}
          aria-describedby={errors.password ? "password-error" : undefined}
          className="mt-2"
        />
        {errors.password && (
          <FieldHint id="password-error" error>
            {errors.password}
          </FieldHint>
        )}
      </div>

      <div>
        <Label required>Bạn đang dạy khối lớp nào?</Label>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((g) => (
            <label
              key={g}
              className="flex cursor-pointer flex-col items-center gap-1 rounded-lg border border-border-strong bg-surface py-2.5 text-body-sm font-semibold text-foreground-secondary transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/10 has-[:checked]:text-primary"
            >
              <input type="radio" name="teachingGrade" value={g} className="sr-only" />
              Lớp {g}
            </label>
          ))}
        </div>
        {errors.teachingGrade && <FieldHint error>{errors.teachingGrade}</FieldHint>}
      </div>

      <div>
        <label className="flex items-start gap-2 text-body-sm text-foreground-secondary">
          <Checkbox name="acceptTerms" className="mt-0.5" />
          <span>
            Tôi đồng ý với{" "}
            <Link href="/dieu-khoan" className="text-primary hover:underline">
              Điều khoản sử dụng
            </Link>{" "}
            và{" "}
            <Link href="/quyen-rieng-tu" className="text-primary hover:underline">
              Chính sách quyền riêng tư
            </Link>
            .
          </span>
        </label>
        {errors.acceptTerms && (
          <FieldHint error>{errors.acceptTerms}</FieldHint>
        )}
      </div>

      <Button type="submit" size="lg" block isLoading={submitting}>
        Tạo tài khoản
      </Button>
    </form>
  );
}
